import { createSelector, createSlice } from "@reduxjs/toolkit";
import { IStateDefaults } from "../../types/shared";
import {
    IAgeLevel,
    IField,
    INotice,
    ISite,
    ISiteSetting,
    IUmpireUser,
} from "../../types/api";
import { authActions } from "../auth/authSlice";
import {
    getFields,
    getNotices,
    getSettings,
    getSites,
    getUmpires,
    updateNotice,
} from "./globalActions";
import { RootState } from "../../store";

interface IGlobalState extends IStateDefaults {
    ageLevels: IAgeLevel[];
    umpires: IUmpireUser[];
    settings: ISiteSetting[];
    notices: Array<INotice>;
    sites: ISite[];
    fields: IField[];
}

const initialState: IGlobalState = {
    loading: false,
    success: false,
    ageLevels: [],
    umpires: [],
    settings: [],
    sites: [],
    notices: [],
    fields: [],
};

const globalSlice = createSlice({
    name: "global",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.ageLevels = [];
                state.umpires = [];
            })
            .addCase(getUmpires.fulfilled, (state, { payload }) => {
                state.umpires = payload.umpires;
            })
            .addCase(getSettings.fulfilled, (state, { payload }) => {
                state.settings = payload;
            })
            .addCase(getNotices.fulfilled, (state, { payload }) => {
                state.notices = payload;
            })
            .addCase(getSites.fulfilled, (state, { payload }) => {
                state.sites = payload;
            })
            .addCase(updateNotice.pending, state => {
                state.loading = true;
            })
            .addCase(updateNotice.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.notices.map(n => {
                    if (n.site === 4) {
                        return payload;
                    } else {
                        return n;
                    }
                });
            })
            .addCase(updateNotice.rejected, state => {
                state.loading = false;
            })
            .addCase(getFields.fulfilled, (state, { payload }) => {
                state.fields = payload;
            });
    },
});

export const globalActions = {
    ...globalSlice.actions,
    getUmpires: getUmpires,
    getSettings: getSettings,
    getNotices: getNotices,
    updateNotice: updateNotice,
    getSites: getSites,
    getFields: getFields,
};

export default globalSlice.reducer;

export const selectAgeLevels = createSelector(
    [(state: RootState) => state.global],
    global => global.ageLevels,
);

export const selectSetting = createSelector(
    [
        (state: RootState) => state.global.settings,
        (state: RootState, settingName: string) => settingName,
    ],
    (settings, settingName) =>
        settings.find(setting => setting.settingName === settingName),
);

export const selectAllSettings = createSelector(
    [(state: RootState) => state.global],
    global => global.settings,
);

export const selectUmpires = createSelector(
    [(state: RootState) => state.global],
    global => global.umpires,
);

export const selectNotice = createSelector(
    [(state: RootState) => state.global],
    global => global.notices.find(n => n.site === 4),
);

export const selectAllNotices = createSelector(
    [(state: RootState) => state.global],
    global => global.notices,
);

export const selectSite = createSelector(
    [
        (state: RootState) => state.global.sites,
        (state: RootState, siteId: number) => siteId,
    ],
    (sites, siteId) => sites.find(site => site.id === siteId),
);

export const selectAllSites = createSelector(
    [(state: RootState) => state.global],
    global => global.sites,
);

export const selectFields = createSelector(
    [(state: RootState) => state.global],
    global => global.fields,
);
